/*
  Moteur d'aperçu des bijoux à charms — DE NADA PARIS
  Partagé par l'atelier « Créer mon bijou » (charms-builder.js) et le bloc d'accueil « À vos charms » (charms-teaser.js) :
  repérage de la chaîne sur sa photo produit, fond studio prolongé, anneaux dorés, détourage des charms, prix.
*/
(() => {
  if (window.DenadaCharms) return;

  // Aperçu : photo produit de la chaîne, analysée dans un repère de 800 × 800
  const W = 1200;
  const RATIO = { bracelet: 0.8, collier: 1 }; // hauteur / largeur de l'aperçu
  const LENGTH = { bracelet: 15, collier: 40 }; // tour de chaîne en cm (hors rallonge), pour l'échelle des charms
  const CHARM_H = 1.5; // hauteur d'un charm standard, en cm
  const A = 800;
  const GOLD_STOPS = [[0, '#F7E4A4'], [0.38, '#DDB35A'], [0.72, '#A97F2F'], [1, '#EDCE85']];
  const GOLD_EDGE = 'rgba(118, 82, 22, 0.85)';
  const BG = ['#F4F4F3', '#E5E4E2'];

  const plain = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00a0/g, ' ').toLowerCase().trim();
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const shortName = (title) => title.split('|').pop().replace(/\u00a0/g, ' ').trim();
  const sizeHint = (title) => {
    const t = plain(title);
    if (/\b(petit|petite|mini)\b/.test(t)) return 0.75;
    if (/\bgrand\b/.test(t)) return 1.15;
    return 1;
  };
  const randomRef = () => `DN-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

  /* ---------- Chaîne : repérage du tracé sur la photo produit ---------- */

  // Partie carrée centrale d'une photo
  const square = (img) => {
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    return [(img.naturalWidth - s) / 2, (img.naturalHeight - s) / 2, s, s];
  };

  // Tracé fermé (rayon par angle autour du centre), parcouru depuis le haut vers la gauche puis le bas :
  // le long du bas, les charms se suivent donc de gauche à droite
  function buildRing(cx, cy, mid, out, gold) {
    const N = mid.length;
    const ring = [];
    for (let j = 0; j < N; j++) {
      const k = (Math.round(N * 0.25) - j + N) % N;
      const a = -Math.PI + ((k + 0.5) / N) * 2 * Math.PI, co = Math.cos(a), si = Math.sin(a);
      ring.push({ x: cx + mid[k] * co, y: cy + mid[k] * si, ox: cx + out[k] * co, oy: cy + out[k] * si });
    }
    ring.push(ring[0]);
    const cum = [0];
    for (let i = 1; i < ring.length; i++) cum.push(cum[i - 1] + Math.hypot(ring[i].x - ring[i - 1].x, ring[i].y - ring[i - 1].y));
    const total = cum[cum.length - 1];
    // point du bord extérieur de la chaîne à l'abscisse s (et point du milieu, pour l'orientation)
    const at = (s) => {
      s = ((s % total) + total) % total;
      let lo = 0, hi = cum.length - 1;
      while (hi - lo > 1) {
        const m = (lo + hi) >> 1;
        if (cum[m] <= s) lo = m; else hi = m;
      }
      const t = (s - cum[lo]) / ((cum[hi] - cum[lo]) || 1), p = ring[lo], q = ring[hi];
      return { x: p.ox + (q.ox - p.ox) * t, y: p.oy + (q.oy - p.oy) * t, mx: p.x + (q.x - p.x) * t, my: p.y + (q.y - p.y) * t };
    };
    let low = 0, lowY = -Infinity;
    ring.forEach((p, i) => { if (p.y > lowY) { lowY = p.y; low = cum[i]; } });
    return { cx, cy, ring, total, at, low, gold };
  }

  // Le métal doré se distingue du fond studio gris par sa couleur ; on en tire un profil rayon / angle lissé
  function analyzeChain(img) {
    const c = document.createElement('canvas');
    c.width = c.height = A;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, ...square(img), 0, 0, A, A);
    let d;
    try { d = x.getImageData(0, 0, A, A).data; } catch (e) { return null; }
    const pts = [];
    for (let py = 0; py < A; py += 2) {
      for (let px = 0; px < A; px += 2) {
        const i = (py * A + px) * 4;
        if (d[i] - d[i + 2] > 38 && d[i] > 90) pts.push([px, py]);
      }
    }
    if (pts.length < 400) return null;
    const gold = { minX: A, maxX: 0, minY: A, maxY: 0 };
    let cx = 0, cy = 0;
    pts.forEach(([px, py]) => {
      cx += px; cy += py;
      gold.minX = Math.min(gold.minX, px); gold.maxX = Math.max(gold.maxX, px);
      gold.minY = Math.min(gold.minY, py); gold.maxY = Math.max(gold.maxY, py);
    });
    cx /= pts.length; cy /= pts.length;
    const N = 180;
    const q = (arr, f) => { const v = arr.slice().sort((a, b) => a - b); return v[Math.min(v.length - 1, Math.floor(f * v.length))]; };
    let mid, out;
    for (let iter = 0; iter < 4; iter++) {
      const bins = Array.from({ length: N }, () => []);
      pts.forEach(([px, py]) => {
        const k = Math.floor(((Math.atan2(py - cy, px - cx) + Math.PI) / (2 * Math.PI)) * N) % N;
        bins[k].push(Math.hypot(px - cx, py - cy));
      });
      const inn = bins.map((b) => (b.length > 3 ? q(b, 0.1) : null));
      out = bins.map((b) => (b.length > 3 ? q(b, 0.9) : null));
      const width = out.map((v, k) => (v == null ? null : v - inn[k]));
      const known = width.filter((v) => v != null);
      if (known.length < N / 2) return null;
      const medW = q(known, 0.5);
      // fermoir, rallonge et médaille élargissent le profil : ces angles sont interpolés depuis leurs voisins
      const valid = width.map((v) => v != null && v < medW * 1.8);
      const fill = (arr) => arr.map((v, k) => {
        if (valid[k]) return v;
        let a = k, b = k, da = 0, db = 0;
        while (!valid[(a + N) % N] && da < N) { a--; da++; }
        while (!valid[b % N] && db < N) { b++; db++; }
        const va = arr[(a + N) % N], vb = arr[b % N];
        return va + (vb - va) * (da / (da + db));
      });
      mid = fill(inn.map((v, k) => (v == null ? null : (v + out[k]) / 2)));
      out = fill(out);
      let sx = 0, sy = 0;
      mid.forEach((r, k) => {
        const a = -Math.PI + ((k + 0.5) / N) * 2 * Math.PI;
        sx += cx + r * Math.cos(a);
        sy += cy + r * Math.sin(a);
      });
      cx = sx / N;
      cy = sy / N;
    }
    const smooth = (arr) => arr.map((_, k) => { let v = 0; for (let j = -3; j <= 3; j++) v += arr[(k + j + N) % N]; return v / 7; });
    return buildRing(cx, cy, smooth(mid), smooth(out), gold);
  }

  // Si la photo ne peut pas être lue : un simple tour de chaîne à la forme du bijou
  function fallbackChain(type) {
    const N = 180, rx = type === 'bracelet' ? 210 : 230, ry = type === 'bracelet' ? 210 : 320;
    const mid = [], out = [];
    for (let k = 0; k < N; k++) {
      const a = -Math.PI + ((k + 0.5) / N) * 2 * Math.PI;
      const r = (rx * ry) / Math.hypot(ry * Math.cos(a), rx * Math.sin(a));
      mid.push(r);
      out.push(r + 6);
    }
    return buildRing(400, type === 'bracelet' ? 400 : 420, mid, out, null);
  }

  // Fond studio prolongé au-delà de la photo (couleurs des bords en dégradé lisse) et photo aux bords fondus
  function backdrop(img) {
    const T = 6, B = 4, n = T + 2 * B;
    const tiny = document.createElement('canvas');
    tiny.width = tiny.height = T;
    const tx = tiny.getContext('2d', { willReadFrequently: true });
    tx.drawImage(img, ...square(img), 0, 0, T, T);
    const td = tx.getImageData(0, 0, T, T).data;
    const ext = document.createElement('canvas');
    ext.width = ext.height = n;
    const ex = ext.getContext('2d'), ed = ex.createImageData(n, n);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const sx = Math.min(T - 1, Math.max(0, x - B)), sy = Math.min(T - 1, Math.max(0, y - B));
        for (let k = 0; k < 4; k++) ed.data[(y * n + x) * 4 + k] = td[(sy * T + sx) * 4 + k];
      }
    }
    ex.putImageData(ed, 0, 0);
    const S = Math.min(1400, square(img)[2]);
    const soft = document.createElement('canvas');
    soft.width = soft.height = S;
    const sc = soft.getContext('2d');
    sc.drawImage(img, ...square(img), 0, 0, S, S);
    sc.globalCompositeOperation = 'destination-in';
    [[S, 0], [0, S]].forEach(([gx, gy]) => {
      const g = sc.createLinearGradient(0, 0, gx, gy);
      g.addColorStop(0, 'rgba(0, 0, 0, 0)');
      g.addColorStop(0.05, '#000');
      g.addColorStop(0.95, '#000');
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      sc.fillStyle = g;
      sc.fillRect(0, 0, S, S);
    });
    return { ext, extOff: B * (A / T), extSize: n * (A / T), soft };
  }

  function goldGradient(ctx, x0, y0, x1, y1) {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    GOLD_STOPS.forEach(([o, c]) => g.addColorStop(o, c));
    return g;
  }

  // Anneau doré en relief
  function strokeGold(ctx, path, th, w, h) {
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = GOLD_EDGE;
    ctx.lineWidth = th * 1.24;
    ctx.stroke(path);
    ctx.strokeStyle = goldGradient(ctx, -w / 2, -h / 2, w / 2, h / 2);
    ctx.lineWidth = th;
    ctx.stroke(path);
  }

  function drawRing(ctx, x, y, r, th) {
    ctx.save();
    ctx.translate(x, y);
    const p = new Path2D();
    p.arc(0, 0, r, 0, Math.PI * 2);
    strokeGold(ctx, p, th, r * 2, r * 2);
    ctx.restore();
  }

  /* ---------- Détourage des photos de charms (fond studio) ---------- */

  function solve(A, b) {
    const n = b.length;
    for (let c = 0; c < n; c++) {
      let p = c;
      for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
      [A[c], A[p]] = [A[p], A[c]];
      [b[c], b[p]] = [b[p], b[c]];
      const d = A[c][c] || 1e-9;
      for (let r = c + 1; r < n; r++) {
        const f = A[r][c] / d;
        for (let k = c; k < n; k++) A[r][k] -= f * A[c][k];
        b[r] -= f * b[c];
      }
    }
    const x = new Array(n).fill(0);
    for (let r = n - 1; r >= 0; r--) {
      let s = b[r];
      for (let k = r + 1; k < n; k++) s -= A[r][k] * x[k];
      x[r] = s / (A[r][r] || 1e-9);
    }
    return x;
  }

  // Fond studio modélisé par une surface quadratique ajustée sur le pourtour de l'image
  function fitBackground(d, S) {
    const feats = (px, py) => {
      const u = px / S - 0.5, v = py / S - 0.5;
      return [1, u, v, u * u, v * v, u * v];
    };
    let samples = [];
    for (let p = 0; p < S; p += 3) {
      for (let k = 0; k < 6; k += 2) {
        samples.push([p, k], [p, S - 1 - k], [k, p], [S - 1 - k, p]);
      }
    }
    const fit = (list) => [0, 1, 2].map((ch) => {
      const A = Array.from({ length: 6 }, () => new Array(6).fill(0));
      const b = new Array(6).fill(0);
      list.forEach(([px, py]) => {
        const f = feats(px, py), y = d[(py * S + px) * 4 + ch];
        for (let i = 0; i < 6; i++) {
          b[i] += f[i] * y;
          for (let j = 0; j < 6; j++) A[i][j] += f[i] * f[j];
        }
      });
      return solve(A, b);
    });
    const evalAt = (coef, px, py) => {
      const f = feats(px, py);
      return coef.map((c) => c.reduce((s, ci, i) => s + ci * f[i], 0));
    };
    let coef = fit(samples);
    // second passage sans les points trop éloignés (charm qui touche le bord)
    samples = samples.filter(([px, py]) => {
      const bg = evalAt(coef, px, py), i = (py * S + px) * 4;
      return Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2]) < 30;
    });
    if (samples.length > 60) coef = fit(samples);
    return (px, py) => evalAt(coef, px, py);
  }

  function detour(img) {
    const S = 360;
    const c = document.createElement('canvas');
    c.width = c.height = S;
    const x = c.getContext('2d', { willReadFrequently: true });
    const r = Math.min(S / img.naturalWidth, S / img.naturalHeight);
    const dw = img.naturalWidth * r, dh = img.naturalHeight * r;
    x.drawImage(img, (S - dw) / 2, (S - dh) / 2, dw, dh);
    let data;
    try { data = x.getImageData(0, 0, S, S); } catch (e) { return { canvas: c, w: S, h: S, ax: 0.5, raw: true }; }
    const d = data.data;

    let transparent = 0;
    for (let p = 0; p < S; p += 6) if (d[(p * S) * 4 + 3] < 250 || d[(S * S - 1 - p) * 4 + 3] < 250) transparent++;
    if (transparent < 4) {
      const bgAt = fitBackground(d, S);
      for (let py = 0; py < S; py++) {
        // le modèle varie lentement : un calcul tous les 4 pixels suffit
        let bg = null;
        for (let px = 0; px < S; px++) {
          if (px % 4 === 0) bg = bgAt(px, py);
          const i = (py * S + px) * 4;
          const dr = d[i] - bg[0], dg = d[i + 1] - bg[1], db = d[i + 2] - bg[2];
          const lum = (dr + dg + db) / 3;
          const chroma = Math.hypot(dr - lum, dg - lum, db - lum);
          const lumD = lum < 0 ? -lum * 0.55 : lum * 0.9; // les ombres grises comptent peu
          const dist = Math.sqrt(chroma * chroma * 1.4 + lumD * lumD);
          const a = clamp01((dist - 7) / 14);
          if (a > 0 && a < 1) {
            // retire la teinte du fond des bords semi-transparents
            d[i] = Math.max(0, Math.min(255, (d[i] - (1 - a) * bg[0]) / a));
            d[i + 1] = Math.max(0, Math.min(255, (d[i + 1] - (1 - a) * bg[1]) / a));
            d[i + 2] = Math.max(0, Math.min(255, (d[i + 2] - (1 - a) * bg[2]) / a));
          }
          d[i + 3] = Math.round(a * d[i + 3]);
        }
      }
      x.putImageData(data, 0, 0);
    }

    // cadre utile : lignes et colonnes contenant au moins 3 pixels opaques
    const rows = new Array(S).fill(0), cols = new Array(S).fill(0);
    for (let py = 0; py < S; py++) {
      for (let px = 0; px < S; px++) {
        if (d[(py * S + px) * 4 + 3] > 128) { rows[py]++; cols[px]++; }
      }
    }
    const first = (arr) => arr.findIndex((v) => v >= 3);
    const last = (arr) => arr.length - 1 - [...arr].reverse().findIndex((v) => v >= 3);
    let minY = first(rows), maxY = last(rows), minX = first(cols), maxX = last(cols);
    if (minY < 0 || minX < 0 || maxX - minX < 12 || maxY - minY < 12) return { canvas: c, w: S, h: S, ax: 0.5, raw: true };
    const pad = 2;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(S - 1, maxX + pad); maxY = Math.min(S - 1, maxY + pad);
    const w = maxX - minX + 1, h = maxY - minY + 1;

    // point d'accroche : milieu des pixels opaques tout en haut du charm
    let sx = 0, n = 0;
    const band = Math.max(3, Math.round(h * 0.07));
    for (let py = minY; py < minY + band; py++) {
      for (let px = minX; px <= maxX; px++) if (d[(py * S + px) * 4 + 3] > 128) { sx += px - minX; n++; }
    }
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    out.getContext('2d').drawImage(c, minX, minY, w, h, 0, 0, w, h);
    return { canvas: out, w, h, ax: n ? sx / n / w : 0.5 };
  }

  /* ---------- Prix au format de la boutique ---------- */

  function formatMoney(cents, format) {
    format = format || '€{{amount_with_comma_separator}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const value = cents / 100;
    const [int, dec] = value.toFixed(2).split('.');
    const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    let out;
    switch (key) {
      case 'amount_no_decimals': out = group(Math.round(value).toString(), ','); break;
      case 'amount_with_comma_separator': out = `${group(int, '.')},${dec}`; break;
      case 'amount_no_decimals_with_comma_separator': out = group(Math.round(value).toString(), '.'); break;
      case 'amount_with_space_separator': out = `${group(int, ' ')},${dec}`; break;
      case 'amount_no_decimals_with_space_separator': out = group(Math.round(value).toString(), ' '); break;
      case 'amount_with_apostrophe_separator': out = `${group(int, "'")}.${dec}`; break;
      default: out = `${group(int, ',')}.${dec}`;
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  window.DenadaCharms = {
    W, RATIO, LENGTH, CHARM_H, A, GOLD_STOPS, GOLD_EDGE, BG,
    plain, esc, clamp01, shortName, sizeHint, randomRef,
    square, buildRing, analyzeChain, fallbackChain, backdrop, goldGradient, strokeGold, drawRing,
    solve, fitBackground, detour, formatMoney
  };
})();
